import type { Client } from "../client/client.js";
import { ProfessionalStatus, ProfessionalType, type Professional } from "./professional.js";
import { UserStatus, type User } from "./user.js";

/**
 * PESSOAS E ACESSOS — uma pessoa, uma linha.
 *
 * O sistema tinha três cadastros para a mesma pessoa: Professional (quem ela
 * é profissionalmente), User (a conta de acesso) e Signatory (a autorização
 * de assinar para um Cliente). Nas três telas antigas, "João Silva" aparecia
 * três vezes, e nada dizia que eram a mesma pessoa.
 *
 * A entidade central é o PROFESSIONAL. User é um acessório dela (0 ou 1), e
 * "signatário" deixa de ser um cadastro: é a capacidade do Professional de
 * assinar, que vem da assinatura registrada e do papel no documento.
 *
 * Este arquivo é PURO — só monta a visão a partir de registros já lidos.
 * Quem lê do repositório é o AdminService, numa consulta agregada.
 */

/**
 * A LINHA DA LISTAGEM. Deliberadamente leve: nenhum blob (assinatura, foto),
 * nenhum histórico, nenhum documento. A lista diz apenas o que a tabela
 * mostra; o cadastro completo é carregado ao abrir a pessoa.
 */
export interface ProfessionalDirectoryView {
  readonly professionalId: string;
  readonly name: string;
  /** Função/cargo técnico (Professional.functionTitle). Não é o perfil de acesso. */
  readonly jobTitle: string;
  readonly profession: string;
  readonly council: string;
  readonly registration: string;
  readonly email: string;

  /** Conta de acesso, quando existir. `null` = pessoa sem acesso ao sistema. */
  readonly userId: string | null;
  readonly username: string | null;
  readonly accountStatus: PeopleAccountStatus;
  /** Perfil de acesso do User (RoleCode). Vazio quando não há conta. */
  readonly roleSummary: string;

  /** Elegível como PLH e Clientes em que de fato responde. */
  readonly isPlh: boolean;
  readonly plhClientIds: readonly string[];

  readonly hasSignature: boolean;
  readonly professionalStatus: ProfessionalStatus;
}

export type PeopleAccountStatus = "SEM_ACESSO" | "ATIVO" | "INATIVO" | "BLOQUEADO" | "CONVIDADO";

export const PEOPLE_ACCOUNT_STATUS_LABELS: Readonly<Record<PeopleAccountStatus, string>> = {
  SEM_ACESSO: "Sem acesso",
  ATIVO: "Ativo",
  INATIVO: "Inativo",
  BLOQUEADO: "Bloqueado",
  CONVIDADO: "Convidado"
};

export type PeopleFilter =
  | "TODOS" | "COM_ACESSO" | "SEM_ACESSO"
  | "INSPETORES" | "TECNICOS" | "ENGENHEIROS" | "PLH"
  | "COM_ASSINATURA" | "SEM_ASSINATURA"
  | "ATIVOS" | "INATIVOS";

export const PEOPLE_FILTERS: ReadonlyArray<{ value: PeopleFilter; label: string }> = [
  { value: "TODOS", label: "Todos" },
  { value: "COM_ACESSO", label: "Com acesso" },
  { value: "SEM_ACESSO", label: "Sem acesso" },
  { value: "INSPETORES", label: "Inspetores" },
  { value: "TECNICOS", label: "Técnicos" },
  { value: "ENGENHEIROS", label: "Engenheiros" },
  { value: "PLH", label: "PLH" },
  { value: "COM_ASSINATURA", label: "Com assinatura" },
  { value: "SEM_ASSINATURA", label: "Sem assinatura" },
  { value: "ATIVOS", label: "Ativos" },
  { value: "INATIVOS", label: "Inativos" }
];

function accountStatusOf(user: User | null): PeopleAccountStatus {
  if (!user) return "SEM_ACESSO";
  if (user.deletedAt) return "INATIVO";
  switch (user.status) {
    case UserStatus.Active: return "ATIVO";
    case UserStatus.Blocked: return "BLOQUEADO";
    case UserStatus.Invited: return "CONVIDADO";
    default: return "INATIVO";
  }
}

/** Primeiro conselho com registro informado — o que a listagem mostra. */
function councilOf(professional: Professional): { council: string; registration: string } {
  const candidates: ReadonlyArray<[string, string | null | undefined]> = [
    ["CREA", professional.crea], ["CFT", professional.cft],
    ["SNQC", professional.snqc], ["SNCC", professional.sncc]
  ];
  for (const [council, registration] of candidates) {
    const value = (registration || "").trim();
    if (value) return { council, registration: value };
  }
  const qualification = professional.qualifications?.find(q => (q.registrationNumber || "").trim());
  if (qualification) return { council: String(qualification.authority), registration: qualification.registrationNumber };
  return { council: "", registration: "" };
}

const professionLabelOf = (professional: Professional): string =>
  String(professional.professionalType ?? "").trim();

/**
 * Monta a listagem. `users` e `clients` entram como listas já lidas, e o
 * cruzamento é feito por índice — uma passada por coleção, sem uma consulta
 * por pessoa (item 32: nada de N+1).
 */
export function buildPeopleDirectory(
  professionals: readonly Professional[],
  users: readonly User[],
  clients: readonly Client[]
): ProfessionalDirectoryView[] {
  const userByProfessionalId = new Map<string, User>();
  for (const user of users) {
    if (user.deletedAt) continue;
    if (user.professionalId) userByProfessionalId.set(String(user.professionalId), user);
  }

  // Clientes em que cada Professional é o PLH responsável. A fonte é o
  // CLIENTE (Client.responsiblePlhProfessionalId) — a página só mostra o que
  // o cadastro do Cliente decidiu, sem criar um segundo vínculo (item 20).
  const plhClientsByProfessionalId = new Map<string, string[]>();
  for (const client of clients) {
    if (client.deletedAt) continue;
    const responsible = client.responsiblePlhProfessionalId ? String(client.responsiblePlhProfessionalId) : "";
    if (!responsible) continue;
    const list = plhClientsByProfessionalId.get(responsible) ?? [];
    list.push(String(client.id));
    plhClientsByProfessionalId.set(responsible, list);
  }

  return professionals
    .filter(professional => !professional.deletedAt)
    .map(professional => {
      const professionalId = String(professional.id);
      // O vínculo vale nos dois sentidos: User.professionalId é o oficial, e
      // Professional.userId cobre os registros gravados pelo outro lado.
      const user = userByProfessionalId.get(professionalId)
        ?? (professional.userId ? users.find(u => !u.deletedAt && String(u.id) === String(professional.userId)) ?? null : null);
      const { council, registration } = councilOf(professional);
      const plhClientIds = plhClientsByProfessionalId.get(professionalId) ?? [];

      return {
        professionalId,
        name: professional.name,
        jobTitle: professional.functionTitle || "",
        profession: professionLabelOf(professional),
        council,
        registration,
        email: professional.email || "",
        userId: user ? String(user.id) : null,
        username: user ? (user.login || user.username || null) : null,
        accountStatus: accountStatusOf(user ?? null),
        roleSummary: user?.profileType ? String(user.profileType) : "",
        // Elegibilidade técnica (plhClientId) OU responsabilidade efetiva.
        // Uma sem a outra é um dado a resolver, não motivo para esconder.
        isPlh: !!professional.plhClientId || plhClientIds.length > 0,
        plhClientIds,
        hasSignature: !!professional.signatureImageId,
        professionalStatus: professional.status
      } satisfies ProfessionalDirectoryView;
    })
    .sort((a, b) => a.name.localeCompare(b.name, "pt-BR", { sensitivity: "base" }));
}

/** Aplica um filtro da barra. `TODOS` não esconde nada. */
export function filterPeopleDirectory(
  rows: readonly ProfessionalDirectoryView[],
  filter: PeopleFilter
): ProfessionalDirectoryView[] {
  switch (filter) {
    case "COM_ACESSO": return rows.filter(r => r.userId !== null);
    case "SEM_ACESSO": return rows.filter(r => r.userId === null);
    // Os filtros seguem o enum real do domínio: "Inspetores" cobre o
    // inspetor de equipamentos e o de END (os dois são inspetores), e
    // "Técnicos" é o técnico de documentação — não existe um tipo
    // "TECHNICIAN" genérico, e inventar um criaria uma segunda taxonomia.
    case "INSPETORES": return rows.filter(r => r.profession === ProfessionalType.Inspector || r.profession === ProfessionalType.NdtInspector);
    case "TECNICOS": return rows.filter(r => r.profession === ProfessionalType.DocumentationTechnician);
    case "ENGENHEIROS": return rows.filter(r => r.profession === ProfessionalType.Engineer);
    case "PLH": return rows.filter(r => r.isPlh);
    case "COM_ASSINATURA": return rows.filter(r => r.hasSignature);
    case "SEM_ASSINATURA": return rows.filter(r => !r.hasSignature);
    case "ATIVOS": return rows.filter(r => r.professionalStatus === ProfessionalStatus.Active);
    case "INATIVOS": return rows.filter(r => r.professionalStatus !== ProfessionalStatus.Active);
    default: return [...rows];
  }
}

/** Busca por nome, login, e-mail ou registro profissional. */
export function searchPeopleDirectory(
  rows: readonly ProfessionalDirectoryView[],
  term: string
): ProfessionalDirectoryView[] {
  const needle = (term || "").trim().toLowerCase();
  if (!needle) return [...rows];
  return rows.filter(r =>
    r.name.toLowerCase().includes(needle)
    || (r.username || "").toLowerCase().includes(needle)
    || r.email.toLowerCase().includes(needle)
    || r.registration.toLowerCase().includes(needle)
    || r.jobTitle.toLowerCase().includes(needle)
  );
}

/**
 * PENDÊNCIA DE MIGRAÇÃO (itens 36 e 37).
 *
 * O sistema NUNCA une duas fichas por nome igual. Quando um User não aponta
 * para nenhum Professional, isto aqui apenas RELATA o caso — com os possíveis
 * correspondentes, se houver — para decisão humana. Unir silenciosamente
 * daria acesso de uma pessoa a outra.
 */
export interface PeopleMigrationPendency {
  readonly kind: "USER_SEM_PROFESSIONAL" | "PROFESSIONAL_COM_USER_INEXISTENTE" | "VINCULO_DIVERGENTE";
  readonly userId: string | null;
  readonly professionalId: string | null;
  readonly name: string;
  readonly detail: string;
  /** Candidatos por nome/e-mail — sugestão para conferência, nunca vínculo automático. */
  readonly candidateProfessionalIds: readonly string[];
}

export function findPeopleMigrationPendencies(
  professionals: readonly Professional[],
  users: readonly User[]
): PeopleMigrationPendency[] {
  const alive = <T extends { deletedAt?: unknown }>(list: readonly T[]) => list.filter(i => !i.deletedAt);
  const livingProfessionals = alive(professionals);
  const livingUsers = alive(users);
  const professionalById = new Map(livingProfessionals.map(p => [String(p.id), p]));
  const norm = (value: string | null | undefined) => (value || "").trim().toLowerCase();

  const pendencies: PeopleMigrationPendency[] = [];

  for (const user of livingUsers) {
    const linked = user.professionalId ? String(user.professionalId) : "";
    if (!linked) {
      // Pode ser um usuário administrativo puro (item 40) — legítimo. Só é
      // pendência quando existe um Professional plausivelmente igual.
      const candidates = livingProfessionals
        .filter(p => (norm(p.name) === norm(user.name) && norm(p.name) !== "") || (norm(p.email) === norm(user.email) && norm(p.email) !== ""))
        .map(p => String(p.id));
      if (candidates.length > 0) {
        pendencies.push({
          kind: "USER_SEM_PROFESSIONAL", userId: String(user.id), professionalId: null, name: user.name,
          detail: "Conta de acesso sem Professional vinculado, com possível correspondente no cadastro de Profissionais. Confirme manualmente antes de vincular.",
          candidateProfessionalIds: candidates
        });
      }
      continue;
    }
    if (!professionalById.has(linked)) {
      pendencies.push({
        kind: "PROFESSIONAL_COM_USER_INEXISTENTE", userId: String(user.id), professionalId: linked, name: user.name,
        detail: `A conta aponta para o Professional '${linked}', que não existe (ou está excluído) no cadastro.`,
        candidateProfessionalIds: []
      });
      continue;
    }
    // Vínculo cruzado: o Professional aponta para OUTRO User.
    const professional = professionalById.get(linked)!;
    if (professional.userId && String(professional.userId) !== String(user.id)) {
      pendencies.push({
        kind: "VINCULO_DIVERGENTE", userId: String(user.id), professionalId: linked, name: user.name,
        detail: `A conta aponta para o Professional '${linked}', mas esse Professional aponta para a conta '${String(professional.userId)}'. Os dois sentidos do vínculo divergem.`,
        candidateProfessionalIds: []
      });
    }
  }

  return pendencies;
}

// ------------------------------------------------- RECONCILIAÇÃO DE VÍNCULO

/**
 * RESOLVER A PENDÊNCIA: achar o Professional real de uma conta órfã.
 *
 * O caso concreto: um User aponta para um `professionalId` que não existe
 * mais (registro legado, migração antiga, cadastro refeito). A conta é
 * legítima e não pode ser apagada; o que falta é a referência certa.
 *
 * A reconciliação automática só acontece com identificação INEQUÍVOCA. Nome
 * não identifica ninguém — homônimo é comum, e unir duas pessoas por engano
 * dá a uma o acesso, a assinatura e a responsabilidade técnica da outra.
 * Havendo mais de um candidato, o sistema não escolhe: devolve a decisão.
 */
export type RelinkMatchCriterion = "REGISTRO_PROFISSIONAL" | "EMAIL_PROFISSIONAL" | "EMAIL_E_REGISTRO";

export interface RelinkCandidate {
  readonly professionalId: string;
  readonly name: string;
  readonly email: string;
  readonly council: string;
  readonly registration: string;
  readonly status: ProfessionalStatus;
  /** Por que este candidato apareceu. */
  readonly matchedBy: readonly RelinkMatchCriterion[];
}

export interface RelinkResolution {
  readonly userId: string;
  readonly invalidProfessionalId: string | null;
  readonly candidates: readonly RelinkCandidate[];
  /** Preenchido só quando há UM candidato por critério inequívoco. */
  readonly automatic: RelinkCandidate | null;
  readonly requiresManualReview: boolean;
  readonly reason: string;
}

const normalize = (value: string | null | undefined) => (value || "").trim().toLowerCase();
/** Registro comparável: só dígitos e letras, para "CREA-SP 28942285" bater com "creasp28942285". */
const normalizeRegistration = (value: string | null | undefined) =>
  (value || "").replace(/[^a-z0-9]/gi, "").toLowerCase();

function registrationsOf(professional: Professional): string[] {
  const raw = [professional.crea, professional.cft, professional.snqc, professional.sncc,
    ...(professional.qualifications ?? []).map(q => q.registrationNumber)];
  return raw.map(normalizeRegistration).filter(v => v.length >= 4);
}

/**
 * Candidatos para uma conta órfã. A ordem dos critérios é a do comando:
 * registro profissional, depois e-mail, depois a combinação dos dois.
 */
export function findRelinkCandidates(
  user: User,
  professionals: readonly Professional[],
  userRegistrationHint?: string | null
): RelinkResolution {
  const invalidProfessionalId = user.professionalId ? String(user.professionalId) : null;
  const living = professionals.filter(p => !p.deletedAt);
  const userEmail = normalize(user.email);
  const hint = normalizeRegistration(userRegistrationHint);

  const scored = living.map(professional => {
    const matchedBy: RelinkMatchCriterion[] = [];
    const registrations = registrationsOf(professional);
    const registrationHit = hint.length >= 4 && registrations.includes(hint);
    const emailHit = userEmail.length > 0 && normalize(professional.email) === userEmail;

    if (registrationHit) matchedBy.push("REGISTRO_PROFISSIONAL");
    if (emailHit) matchedBy.push("EMAIL_PROFISSIONAL");
    if (registrationHit && emailHit) matchedBy.push("EMAIL_E_REGISTRO");

    const { council, registration } = councilOf(professional);
    return {
      professionalId: String(professional.id), name: professional.name, email: professional.email || "",
      council, registration, status: professional.status, matchedBy
    } satisfies RelinkCandidate;
  }).filter(c => c.matchedBy.length > 0);

  // Já vinculado a outro User? Não é candidato — seria roubar a identidade.
  const taken = new Set(
    living.filter(p => p.userId && String(p.userId) !== String(user.id)).map(p => String(p.id))
  );
  const candidates = scored.filter(c => !taken.has(c.professionalId));

  if (candidates.length === 0) {
    return {
      userId: String(user.id), invalidProfessionalId, candidates: [], automatic: null, requiresManualReview: true,
      reason: "Nenhum Professional corresponde a esta conta por registro profissional ou e-mail. Vincule manualmente a um cadastro existente ou crie o Professional."
    };
  }
  if (candidates.length > 1) {
    return {
      userId: String(user.id), invalidProfessionalId, candidates, automatic: null, requiresManualReview: true,
      reason: `${candidates.length} cadastros correspondem a esta conta. A escolha é manual — o sistema não decide entre candidatos igualmente plausíveis.`
    };
  }
  const only = candidates[0]!;
  return {
    userId: String(user.id), invalidProfessionalId, candidates, automatic: only, requiresManualReview: false,
    reason: `Correspondência inequívoca por ${only.matchedBy.includes("EMAIL_E_REGISTRO") ? "e-mail e registro profissional" : only.matchedBy.includes("REGISTRO_PROFISSIONAL") ? "registro profissional" : "e-mail profissional"}.`
  };
}
